/**
 * Scroll Animations Module
 *
 * GSAP ScrollTrigger animations for page elements.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class ScrollAnimations {
  constructor() {
    this.init();
  }

  /**
   * Initialize all scroll animations
   */
  init() {
    // Set initial states immediately before any rendering
    this.setInitialStates();

    // Then set up scroll-triggered animations
    this.fadeUp();
    this.fadeIn();
    this.scaleIn();
    this.staggerItems();
    this.parallaxImages();
    this.sectionTransitions();

    // Mark body as ready for animations
    document.body.classList.add('gsap-ready');
  }

  /**
   * Set initial animation states with GSAP
   * Content stays visible - only subtle transforms for polish
   */
  setInitialStates() {
    // All animated elements start visible with subtle offset
    // This ensures content is always visible, animations are progressive enhancement
    gsap.set('[data-animate="fade-up"]', { y: 20 });
    gsap.set('[data-animate="scale-in"]', { scale: 0.98 });
    gsap.set('[data-stagger-item]', { y: 20 });
    // fade-in elements don't need initial state - they animate opacity only
  }

  /**
   * Fade up animation - subtle slide from below with opacity
   */
  fadeUp() {
    gsap.utils.toArray('[data-animate="fade-up"]').forEach((element) => {
      gsap.to(element, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 92%',
          toggleActions: 'play none none none',
        },
      });
    });
  }

  /**
   * Fade in animation - gentle opacity reveal
   */
  fadeIn() {
    gsap.utils.toArray('[data-animate="fade-in"]').forEach((element) => {
      // Start slightly dimmed, animate to full opacity
      gsap.fromTo(
        element,
        { opacity: 0.7 },
        {
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  /**
   * Scale in animation - subtle scale reveal with opacity
   */
  scaleIn() {
    gsap.utils.toArray('[data-animate="scale-in"]').forEach((element) => {
      gsap.to(element, {
        scale: 1,
        opacity: 1,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
      });
    });
  }

  /**
   * Stagger animation for multiple items - elegant cascade reveal with opacity
   */
  staggerItems() {
    gsap.utils.toArray('[data-animate="stagger"]').forEach((container) => {
      const items = container.querySelectorAll('[data-stagger-item]');

      if (items.length === 0) return;

      gsap.to(items, {
        y: 0,
        opacity: 1,
        duration: 0.7,
        stagger: 0.08,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: container,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });
    });
  }

  /**
   * Parallax effect for images
   */
  parallaxImages() {
    gsap.utils.toArray('[data-parallax]').forEach((element) => {
      const speed = parseFloat(element.dataset.parallax) || 0.2;
      const direction = element.dataset.parallaxDirection || 'vertical';

      const props =
        direction === 'horizontal'
          ? { x: -100 * speed, endX: 100 * speed }
          : { y: -100 * speed, endY: 100 * speed };

      gsap.fromTo(
        element,
        direction === 'horizontal' ? { x: props.x } : { y: props.y },
        {
          ...(direction === 'horizontal' ? { x: props.endX } : { y: props.endY }),
          ease: 'none',
          scrollTrigger: {
            trigger: element.parentElement || element,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });
  }

  /**
   * Section transition effects (for dark sections)
   */
  sectionTransitions() {
    const darkSections = document.querySelectorAll('[data-theme="dark"]');

    darkSections.forEach((section) => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top 60%',
        end: 'bottom 40%',
        onEnter: () => document.body.classList.add('dark-section-active'),
        onLeave: () => document.body.classList.remove('dark-section-active'),
        onEnterBack: () => document.body.classList.add('dark-section-active'),
        onLeaveBack: () => document.body.classList.remove('dark-section-active'),
      });
    });
  }

  /**
   * Destroy all ScrollTriggers
   */
  destroy() {
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  }
}
